import { Modal, Button, Stack, Text, Group } from '@mantine/core'
import { useState } from 'react'

type Props = {
  opened: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  /** Packet being confirmed, e.g. 'Open Trade Packet' — copy must name it. */
  packetName?: string
  /** LOS-6471: override title/body/CTA for re-send and retry popups. */
  title?: string
  body?: string
  confirmLabel?: string
}

export default function ConfirmSendModal({
  opened,
  onClose,
  onConfirm,
  packetName,
  title,
  body,
  confirmLabel,
}: Props) {
  const [loading, setLoading] = useState(false)
  const modalPadding = 20

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm()
      onClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      withinPortal
      size={700}
      overlayProps={{ color: '#000000', opacity: 0.5 }}
      styles={{
        content: {
          borderRadius: 8,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.18)',
          width: 'min(700px, calc(100vw - 32px))',
        },
        body: {
          padding: `${modalPadding}px`,
        },
      }}
    >
      <Stack
        spacing={0}
        sx={{
          minHeight: 148,
          justifyContent: 'space-between',
        }}
      >
        <Text
          sx={{
            color: '#001565',
            fontFamily: 'Plus Jakarta Sans',
            fontSize: 18,
            fontWeight: 600,
            lineHeight: '28px',
          }}
        >
          {title ?? 'Confirm Document Send'}
        </Text>

        <Text
          sx={{
            color: '#413E3E',
            fontFamily: 'Plus Jakarta Sans',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
          }}
        >
          {body ??
            (packetName
              ? `Confirm the ${packetName} was sent. This will mark it as Sent.`
              : 'Are you sure you want to send this document? The email has been prepared and is ready to send.')}
        </Text>

        <Group position="right" spacing={12}>
          <Button
            variant="filled"
            onClick={onClose}
            disabled={loading}
            sx={{
              minWidth: 84,
              height: 36,
              padding: '0 16px',
              borderRadius: 4,
              backgroundColor: '#F3F4F6',
              color: '#413E3E',
              fontFamily: 'Plus Jakarta Sans',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '150%',
              '&:hover': {
                backgroundColor: '#E5E7EB',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirm}
            loading={loading}
            sx={{
              minWidth: 116,
              height: 36,
              padding: '0 16px',
              borderRadius: 4,
              backgroundColor: '#32439A',
              color: '#FFFFFF',
              fontFamily: 'Plus Jakarta Sans',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '150%',
              '&:hover': {
                backgroundColor: '#2A3682',
              },
            }}
          >
            {confirmLabel ?? 'Confirm Send'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
